import { useEffect, RefObject } from "react";

import { isModifierKeyPressed } from "@/utils/shortcuts";

interface UseFindShortcutMaskProps {
  searchInputRef?: RefObject<HTMLInputElement>;
  enabled?: boolean;
}

/**
 * Hook that overrides the browser's default CTRL+F (CMD+F on Mac) behavior
 * to focus a search input instead of opening the browser's find dialog.
 */
export const useFindShortcutMask = ({
  searchInputRef,
  enabled = true,
}: UseFindShortcutMaskProps = {}) => {
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for CTRL+F (Windows/Linux) or CMD+F (Mac)
      const isFindShortcut =
        isModifierKeyPressed(event) &&
        event.key.toLowerCase() === "f" &&
        !event.shiftKey &&
        !event.altKey;

      if (isFindShortcut) {
        event.preventDefault();
        event.stopPropagation();

        // Focus the search input if provided
        if (searchInputRef?.current) {
          searchInputRef.current.focus();
          searchInputRef.current.select();
        }
      }
    };

    // Add listener to document to capture the event before it reaches the browser
    document.addEventListener("keydown", handleKeyDown, { capture: true });

    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [searchInputRef, enabled]);
};
